import { Routes, Route, Navigate, useParams, useLocation, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useState, useEffect } from 'react';
import { ProtectedRoute } from './ProtectedRoute';
import { ProtectedLayout, useSidebar } from './ProtectedLayout';
import { DashboardLayout } from './DashboardLayout';
import { useDashboard } from '@/src/views/dashboard/context';
import { ChartColumn, Table2, Search, MoreHorizontal, ChevronDown, ChevronUp, Info, ExternalLink } from 'lucide-react';
import { Card } from '@/src/components/ui/shadcn/card';
import { Button } from '@/src/components/ui/shadcn/button';
import { Badge } from '@/src/components/ui/shadcn/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/src/components/ui/shadcn/tooltip';
import { moreItems, collapsedMoreItems } from '@/src/components/ui/layout/more-dropdown';
import { useFindTask } from '@/src/api/tasks';
import { get, uniqBy } from 'lodash';
import type { FilterState } from '@/src/views/dashboard/types';

// ═══════════════════════════════════════════════════════════════
// Dashboard Tab Components (lazy loaded)
// Paths match the Next.js page imports exactly
// ═══════════════════════════════════════════════════════════════

// Overview
const DashboardOverviewContent = lazy(() =>
  import('@/src/views/dashboard/components/dashboard-overview-content').then((m) => ({
    default: m.DashboardOverviewContent,
  }))
);

// Reports
const DashboardReports = lazy(() =>
  import('@/src/views/dashboard/components/reports').then((m) => ({ default: m.DashboardReports }))
);
const Drilldown = lazy(() => import('@/src/views/reports/drilldown'));
const InteractionReports = lazy(() => import('@/src/views/reports/interactions'));
const InteractionLookup = lazy(() => import('@/src/views/reports/interaction-lookup'));

// Campaign Management
const DashboardCampaignsManagement = lazy(() =>
  import('@/src/views/dashboard/components/dashboard-campaigns-management').then((m) => ({
    default: m.DashboardCampaignsManagement,
  }))
);
const DashboardRotations = lazy(() => import('@/src/views/rotations/list'));
const OfferListView = lazy(() =>
  import('@/src/views/offers/list').then((m) => ({ default: m.default }))
);
const LandingPagesList = lazy(() =>
  import('@/src/views/landing-pages/list').then((m) => ({ default: m.LandingPagesList }))
);

// Traffic & Networks
const TrafficSourcesListView = lazy(() =>
  import('@/src/views/traffic/list').then((m) => ({ default: m.TrafficSourcesListView }))
);
const NetworksListView = lazy(() => import('@/src/views/networks/list'));

// Settings & Configuration
const DomainsListView = lazy(() =>
  import('@/src/views/more/domains/list').then((m) => ({ default: m.DomainsListView }))
);
const IntegrationsList = lazy(() =>
  import('@/src/views/more/integration/list').then((m) => ({ default: m.IntegrationsList }))
);
const TokensList = lazy(() =>
  import('@/src/views/more/tokens/list').then((m) => ({ default: m.TokensList }))
);
const PixelsView = lazy(() =>
  import('@/src/views/more/pixels').then((m) => ({ default: m.PixelsView }))
);

// Automations & Activity
const DashboardAutomations = lazy(() =>
  import('@/src/views/dashboard/components/automations').then((m) => ({
    default: m.DashboardAutomations,
  }))
);
const DashboardActivityMonitor = lazy(() =>
  import('@/src/views/dashboard/components/activity-monitor/dashboard-activity-monitor').then((m) => ({
    default: m.DashboardActivityMonitor,
  }))
);

// More section
const UsersListView = lazy(() =>
  import('@/src/views/more/users/list').then((m) => ({ default: m.UsersListView }))
);
const EditUsersView = lazy(() => import('@/src/views/more/users/edit'));
const CreateUsersView = lazy(() => import('@/src/views/more/users/create'));
const ConversionsPage = lazy(() => import('@/src/views/more/conversions/update'));
const CostUpdateView = lazy(() => import('@/src/views/more/cost-update'));
const BlockedTrafficListView = lazy(() =>
  import('@/src/views/more/blocked-traffic/list').then((m) => ({ default: m.BlockedTrafficListView }))
);
const DashboardProcessLogs = lazy(() =>
  import('@/src/views/dashboard/components/activity-monitor/dashboard-process-logs').then((m) => ({
    default: m.DashboardProcessLogs,
  }))
);
const DashboardAdsCreationLogs = lazy(() =>
  import('@/src/views/dashboard/components/activity-monitor/dashboard-ads-creation-logs').then((m) => ({
    default: m.DashboardAdsCreationLogs,
  }))
);
const DraftsContent = lazy(() =>
  import('@/src/views/campaigns/drafts').then((m) => ({ default: m.DraftsContent }))
);

// Campaign Editor & Wizard
const CampaignEdit = lazy(() => import('@/src/views/campaigns/edit'));
const CampaignWizard = lazy(() =>
  import('@/src/views/campaigns/wizard').then((m) => ({ default: m.CampaignWizard }))
);

// Ads Builder
const AdsBuilder = lazy(() => import('@/src/views/ads-builder'));
const AdsBuilderNamed = lazy(() =>
  import('@/src/views/ads-builder').then((m) => ({ default: m.AdsBuilder }))
);
const LoadingModal = lazy(() => import('@/src/views/ads-builder/components/tabs/common/loading-modal'));

// ═══════════════════════════════════════════════════════════════
// Standalone Page Components
// ═══════════════════════════════════════════════════════════════

const SignIn = lazy(() => import('@/src/views/auth/signin'));
const SignUp = lazy(() => import('@/src/views/auth/signup'));
const UserOnboarding = lazy(() => import('@/src/views/auth/user-onboarding'));
const Onboarding = lazy(() => import('@/src/views/auth/onboarding'));
const Profile = lazy(() => import('@/src/views/profile'));
const Campaigns = lazy(() => import('@/src/views/campaigns'));
const RotationEdit = lazy(() => import('@/src/views/rotations/edit-refactor'));
const OfferEdit = lazy(() => import('@/src/views/offers/edit'));
const OfferCreate = lazy(() => import('@/src/views/offers/create'));
const NetworkCreate = lazy(() => import('@/src/views/networks/create'));
// GrapesJS Landing Page Editor (same as production) — wrapped with withTakeover for collaborative editing
const EditorGrapesWithTakeover = lazy(() =>
  Promise.all([
    import('@/src/components/clk-grape-editor'),
    import('@/src/components/hoc/with-takeover'),
  ]).then(([editorMod, takeoverMod]) => ({
    default: takeoverMod.withTakeover(editorMod.EditorGrapes),
  }))
);
const Settings = lazy(() => import('@/src/views/more/settings'));
const Support = lazy(() => import('@/src/views/more/support'));
const Webhooks = lazy(() => import('@/src/views/more/webhooks'));

// Knowledge
const KnowledgeView = lazy(() =>
  import('@/src/views/more/knowledge').then((m) => ({ default: m.KnowledgeView }))
);
const KnowledgeDetailView = lazy(() => import('@/src/views/more/knowledge/detail'));
const KnowledgeEditorWithTakeover = lazy(() =>
  Promise.all([
    import('@/src/components/knowledge-studio-editor'),
    import('@/src/components/hoc/with-takeover'),
  ]).then(([editorMod, takeoverMod]) => ({
    default: takeoverMod.withTakeover(editorMod.KnowledgeStudioEditorFullPage),
  }))
);

// Workflows, Roadmap, Change Log
const WorkflowsListView = lazy(() => import('@/src/views/workflows/list'));
const RoadmapView = lazy(() => import('@/src/views/more/roadmap'));
const ChangeLogView = lazy(() => import('@/src/views/more/change-log'));

// Version management
const VersionPage = lazy(() => import('@/src/views/more/version'));

// Tag Manager
const TagManagerPage = lazy(() => import('@/src/views/tag-manager'));

// Error Pages
const NotFoundPage = lazy(() => import('@/src/views/errors/not-found'));

// Testing Pages
const PresenceTestPage = lazy(() => import('@/src/views/testing/presence'));
const RotationStabilityTestPage = lazy(() => import('@/src/views/testing/rotations'));

// Placeholder Pages
const AutomatedLogsPage = lazy(() => import('@/src/views/more/automated-logs'));
const MonitoringPage = lazy(() => import('@/src/views/more/monitoring'));
const SecurityPage = lazy(() => import('@/src/views/more/security'));
const NotificationsPage = lazy(() => import('@/src/views/more/notifications'));

// Loading fallback
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// Tab loader (smaller, for dashboard content)
const TabLoader = () => (
  <div className="flex items-center justify-center p-8 min-h-[200px]">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
  </div>
);

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/auth/signin" element={<SignIn />} />
        <Route path="/auth/signup" element={<SignUp />} />
        <Route path="/user-onboarding" element={<UserOnboarding />} />
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Protected routes with layout */}
        <Route element={<ProtectedRoute><ProtectedLayout /></ProtectedRoute>}>

          {/* ═══════════════════════════════════════════════════════════════
              Dashboard with nested routes (layout-based architecture)
              ═══════════════════════════════════════════════════════════════ */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            {/* Default dashboard view (overview) */}
            <Route index element={<DashboardOverviewWrapper />} />

            {/* Reports */}
            <Route path="reports" element={<DashboardReportsWrapper />} />
            <Route path="drilldown-reports" element={
              <Suspense fallback={<TabLoader />}>
                <Drilldown hideSpotlight hideSidebarToggle hideTitle />
              </Suspense>
            } />
            <Route path="interaction-reports" element={
              <Suspense fallback={<TabLoader />}>
                <InteractionReports hideSpotlight hideSidebarToggle hideTitle />
              </Suspense>
            } />
            <Route path="interaction-lookup" element={
              <Suspense fallback={<TabLoader />}>
                <InteractionLookup />
              </Suspense>
            } />

            {/* Campaign Management */}
            <Route path="campaigns" element={<DashboardCampaignsWrapper />} />
            <Route path="rotations" element={
              <Suspense fallback={<TabLoader />}>
                <DashboardRotations />
              </Suspense>
            } />
            <Route path="offers" element={<DashboardOffersWrapper />} />
            <Route path="landing-pages" element={<DashboardLandingPagesWrapper />} />

            {/* Traffic & Networks */}
            <Route path="traffic-sources" element={
              <Suspense fallback={<TabLoader />}>
                <TrafficSourcesListView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="affiliate-networks" element={
              <Suspense fallback={<TabLoader />}>
                <NetworksListView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />

            {/* Settings & Configuration */}
            <Route path="domains" element={
              <Suspense fallback={<TabLoader />}>
                <DomainsListView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="integrations" element={
              <Suspense fallback={<TabLoader />}>
                <IntegrationsList hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="tokens" element={
              <Suspense fallback={<TabLoader />}>
                <TokensList hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="pixels" element={
              <Suspense fallback={<TabLoader />}>
                <PixelsView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />

            {/* Automations & Activity */}
            <Route path="automations" element={
              <Suspense fallback={<TabLoader />}>
                <DashboardAutomations />
              </Suspense>
            } />
            <Route path="activity" element={
              <Suspense fallback={<TabLoader />}>
                <DashboardActivityMonitor />
              </Suspense>
            } />

            {/* More section */}
            <Route path="users" element={
              <Suspense fallback={<TabLoader />}>
                <UsersListView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="conversions" element={
              <Suspense fallback={<TabLoader />}>
                <ConversionsPage hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="cost-update" element={
              <Suspense fallback={<TabLoader />}>
                <CostUpdateView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="blocked-traffic" element={
              <Suspense fallback={<TabLoader />}>
                <BlockedTrafficListView hideSpotlight hideSidebarToggle />
              </Suspense>
            } />
            <Route path="process-logs" element={
              <Suspense fallback={<TabLoader />}>
                <DashboardProcessLogs />
              </Suspense>
            } />
            <Route path="ads-creation-logs" element={
              <Suspense fallback={<TabLoader />}>
                <DashboardAdsCreationLogs />
              </Suspense>
            } />
            <Route path="drafts" element={
              <Suspense fallback={<TabLoader />}>
                <DraftsContent />
              </Suspense>
            } />

            {/* Campaign Editor (dynamic route) */}
            <Route path="campaigns-edit/:platform/:id" element={
              <Suspense fallback={<TabLoader />}>
                <CampaignEdit hideSpotlight />
              </Suspense>
            } />

            {/* Wizard */}
            <Route path="wizard" element={
              <Suspense fallback={<TabLoader />}>
                <CampaignWizard />
              </Suspense>
            } />

            {/* Ads Builder (inside dashboard) */}
            <Route path="ads-builder" element={
              <Suspense fallback={<TabLoader />}>
                <AdsBuilder />
              </Suspense>
            } />
          </Route>

          {/* ═══════════════════════════════════════════════════════════════
              Standalone pages (outside dashboard layout)
              ═══════════════════════════════════════════════════════════════ */}
          <Route path="/campaigns/*" element={<Campaigns />} />
          <Route path="/rotations/:id" element={<RotationEdit />} />
          <Route path="/offers/create" element={<OfferCreate />} />
          <Route path="/offers/:id" element={<OfferEdit />} />
          <Route path="/networks/create" element={<NetworkCreate />} />
          <Route path="/landing-pages/:id/editor" element={<LandingPageEditorWrapper />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/ads-builder/:id" element={<AdsBuilderEditWrapper />} />
          <Route path="/ads-builder/*" element={<AdsBuilder />} />
          <Route path="/campaigns/create-wizard-2" element={<CampaignWizardWrapper />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/support" element={<Support />} />
          <Route path="/webhooks" element={<Webhooks />} />
          <Route path="/users/:id" element={<EditUsersView />} />
          <Route path="/users/create" element={<CreateUsersView />} />
          {/* Knowledge */}
          <Route path="/knowledge" element={<Suspense fallback={<PageLoader />}><KnowledgeView /></Suspense>} />
          <Route path="/knowledge/:id" element={<Suspense fallback={<PageLoader />}><KnowledgeDetailView /></Suspense>} />
          <Route path="/knowledge/:id/editor" element={<KnowledgeEditorWrapper />} />
          {/* Workflows */}
          <Route path="/workflows" element={<Suspense fallback={<PageLoader />}><WorkflowsListView /></Suspense>} />
          {/* More pages */}
          <Route path="/roadmap" element={<Suspense fallback={<PageLoader />}><RoadmapView /></Suspense>} />
          <Route path="/change-log" element={<Suspense fallback={<PageLoader />}><ChangeLogView /></Suspense>} />
          <Route path="/version" element={<Suspense fallback={<PageLoader />}><VersionPage /></Suspense>} />
          <Route path="/automated-logs" element={<AutomatedLogsPage />} />
          <Route path="/monitoring" element={<MonitoringPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Reports menu page */}
          <Route path="/reports" element={<ReportsMenuPage />} />
          {/* More options page */}
          <Route path="/more" element={<MorePage />} />
          {/* Tag Manager */}
          <Route path="/tag-manager" element={<TagManagerPage />} />
          {/* Testing Routes - Only available in non-production environments */}
          {process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' && (
            <>
              <Route path="/testing/presence/:id" element={<PresenceTestPage />} />
              <Route path="/testing/rotations/:id" element={<RotationStabilityTestPage />} />
            </>
          )}
        </Route>

        {/* ═══════════════════════════════════════════════════════════════
            Redirects (matching Next.js redirects from next.config.js)
            ═══════════════════════════════════════════════════════════════ */}

        {/* Reports redirects */}
        <Route path="/reports/drilldown" element={<RedirectWithParams to="/dashboard/drilldown-reports" />} />
        <Route path="/reports/interactions" element={<RedirectWithParams to="/dashboard/interaction-reports" />} />
        <Route path="/reports/interaction-lookup" element={<RedirectWithParams to="/dashboard/interaction-lookup" />} />
        <Route path="/reports/interaction-lookup/:id" element={<InteractionLookupRedirect />} />

        {/* Phase 6A: Rotations and Offers */}
        <Route path="/rotations" element={<RedirectWithParams to="/dashboard/rotations" />} />
        <Route path="/offers" element={<RedirectWithParams to="/dashboard/offers" />} />

        {/* Phase 6B: Landing Pages, Traffic Sources, Affiliate Networks */}
        <Route path="/landing-pages" element={<RedirectWithParams to="/dashboard/landing-pages" />} />
        <Route path="/networks" element={<RedirectWithParams to="/dashboard/affiliate-networks" />} />
        <Route path="/traffics" element={<RedirectWithParams to="/dashboard/traffic-sources" />} />

        {/* Phase 6C: Domains, Integrations, Tokens, Pixels */}
        <Route path="/domains" element={<RedirectWithParams to="/dashboard/domains" />} />
        <Route path="/integrations" element={<RedirectWithParams to="/dashboard/integrations" />} />
        <Route path="/tokens" element={<RedirectWithParams to="/dashboard/tokens" />} />
        <Route path="/pixels" element={<RedirectWithParams to="/dashboard/pixels" />} />

        {/* More menu redirects */}
        <Route path="/users" element={<RedirectWithParams to="/dashboard/users" />} />
        <Route path="/cost-update" element={<RedirectWithParams to="/dashboard/cost-update" />} />
        <Route path="/conversions" element={<RedirectWithParams to="/dashboard/conversions" />} />
        <Route path="/process-logs" element={<RedirectWithParams to="/dashboard/process-logs" />} />

        {/* Phase 6G: Campaign Editors (dynamic route redirects) */}
        <Route path="/campaigns/edit/google/:id" element={<CampaignEditRedirect platform="google" />} />
        <Route path="/campaigns/edit/bing/:id" element={<CampaignEditRedirect platform="bing" />} />
        <Route path="/campaigns/edit/mediago/:id" element={<CampaignEditRedirect platform="mediago" />} />
        <Route path="/campaigns/edit/mgid/:id" element={<CampaignEditRedirect platform="mgid" />} />
        <Route path="/campaigns/edit/newsbreak/:id" element={<CampaignEditRedirect platform="newsbreak" />} />
        <Route path="/campaigns/edit/outbrain/:id" element={<CampaignEditRedirect platform="outbrain" />} />
        <Route path="/campaigns/edit/revcontent/:id" element={<CampaignEditRedirect platform="revcontent" />} />
        <Route path="/campaigns/edit/taboola/:id" element={<CampaignEditRedirect platform="taboola" />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Catch-all - 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

// ═══════════════════════════════════════════════════════════════
// Wrapper Components (use dashboard context)
// ═══════════════════════════════════════════════════════════════

function DashboardOverviewWrapper() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    filters,
    handleFilterChange,
    realTimeConfig,
    showMetricCards,
    isCompactMetrics,
    showPerformanceChart,
    showTrafficSource,
    showCategories,
    showGeoMap,
    showDevices,
    showOffers,
    showLandingPages,
    showSuspicious,
    showMediaBuyers,
    showCampaignList,
    showProfitabilityCards,
    controlOrder,
    filterOptions,
    filterData,
  } = useDashboard();

  // Handle legacy ?tab= query params and redirect to new path-based routing
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== 'overview') {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('tab');
      const queryString = params.toString();
      navigate(`/dashboard/${tab}${queryString ? `?${queryString}` : ''}`, { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div data-testid="dashboard-overview-page">
      <Suspense fallback={<TabLoader />}>
        <DashboardOverviewContent
          filters={{
            ...filters,
            campaigns:
              filters?.campaigns?.length === 0
                ? (filterOptions?.campaigns?.filter((c) => c?.status === 'active') as FilterState['campaigns'])
                : filters.campaigns,
          }}
          showMetricCards={showMetricCards}
          isCompactMetrics={isCompactMetrics}
          showPerformanceChart={showPerformanceChart}
          showTrafficSource={showTrafficSource}
          showCategories={showCategories}
          showGeoMap={showGeoMap}
          showDevices={showDevices}
          showOffers={showOffers}
          showLandingPages={showLandingPages}
          showSuspicious={showSuspicious}
          showMediaBuyers={showMediaBuyers}
          showCampaignList={showCampaignList}
          showProfitabilityCards={showProfitabilityCards}
          controlOrder={controlOrder}
          onFilterChange={handleFilterChange}
          realTimeConfig={realTimeConfig}
          rotations={(filterData?.rotations ?? []).filter((r): r is typeof r & { _id: string } => !!r._id)}
        />
      </Suspense>
    </div>
  );
}

function DashboardReportsWrapper() {
  const { filters, handleFilterChange, realTimeConfig } = useDashboard();

  return (
    <div data-testid="reports-page-container">
      <Suspense fallback={<TabLoader />}>
        <DashboardReports
          filters={{
            ...filters,
            timezone: {
              ...filters.timezone,
              value: filters.timezone.value ?? 0,
            },
          }}
          onFilterChange={handleFilterChange}
          realTimeConfig={realTimeConfig}
        />
      </Suspense>
    </div>
  );
}

function DashboardCampaignsWrapper() {
  const {
    filters,
    handleFilterChange,
    realTimeConfig,
    filterOptions,
    showMetricCards,
    isCompactMetrics,
    showPerformanceChart,
    showProfitabilityCards,
    showCategories,
    showCampaignList,
    controlOrder,
  } = useDashboard();

  return (
    <div data-testid="campaigns-page-container">
      <Suspense fallback={<TabLoader />}>
        <DashboardCampaignsManagement
          showMetricCards={showMetricCards}
          isCompactMetrics={isCompactMetrics}
          showPerformanceChart={showPerformanceChart}
          showProfitabilityCards={showProfitabilityCards}
          showCategories={showCategories}
          showCampaignList={showCampaignList}
          filters={{
            ...filters,
            campaigns:
              filters?.campaigns?.length === 0
                ? (filterOptions?.campaigns?.filter((c) => c?.status === 'active') as FilterState['campaigns'])
                : filters.campaigns,
          }}
          onFilterChange={handleFilterChange}
          initialFilters={[]}
          controlOrder={controlOrder}
          realTimeConfig={realTimeConfig}
        />
      </Suspense>
    </div>
  );
}

function DashboardOffersWrapper() {
  const { realTimeConfig } = useDashboard();

  return (
    <div data-testid="offers-page-container">
      <Suspense fallback={<TabLoader />}>
        <OfferListView
          hideSpotlight
          hideSidebarToggle
          realTimeConfig={realTimeConfig}
        />
      </Suspense>
    </div>
  );
}

function DashboardLandingPagesWrapper() {
  const { realTimeConfig } = useDashboard();

  return (
    <div data-testid="landing-pages-page-container">
      <Suspense fallback={<TabLoader />}>
        <LandingPagesList
          hideSpotlight
          realTimeConfig={realTimeConfig}
        />
      </Suspense>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Landing Page Editor Wrapper
// ═══════════════════════════════════════════════════════════════

function LandingPageEditorWrapper() {
  const { id } = useParams();

  if (!id) {
    return <Navigate to="/dashboard/landing-pages" replace />;
  }

  return (
    <div className="w-full h-full">
      <Suspense fallback={<PageLoader />}>
        <EditorGrapesWithTakeover
          landingPageId={id}
          takeoverConfig={{
            id,
            channelName: `landingpage-${id}`,
          }}
        />
      </Suspense>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Ads Builder Edit Wrapper (task loading + props)
// ═══════════════════════════════════════════════════════════════

function AdsBuilderEditWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();

  if (!id) {
    navigate('/ads-builder');
    return null;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <AdsBuilderEditContent id={id} />
    </Suspense>
  );
}

function AdsBuilderEditContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const { task, refetch, isLoading } = useFindTask(id);

  if (isLoading) {
    return (
      <Suspense fallback={<PageLoader />}>
        <LoadingModal />
      </Suspense>
    );
  }

  if (task?.status !== 'DRAFT') {
    navigate(`/ads-builder?section=published-logs&id=${task!._id}`, { replace: true });
    return null;
  }

  const getCampaigns = () => {
    const campaigns = get(task, 'data.campaigns', []);
    const trafficSourceSelected = get(task, 'data.trafficSourceSelected', null);
    const canCreateAdsByAdGroup = get(trafficSourceSelected, 'api_functionality.multiple_ad_groups_supported', false);

    if (!canCreateAdsByAdGroup) return campaigns;
    return uniqBy(campaigns, '_id');
  };

  return (
    <div className="flex flex-col h-screen p-4">
      <AdsBuilderNamed
        initialBuilderState={get(task, 'data.builder')}
        initialFormState={{
          campaigns: getCampaigns(),
          trafficSourceSelected: get(task, 'data.trafficSourceSelected', null),
          assets: get(task, 'data.builder.assets', []),
          headlines: get(task, 'data.builder.headlines', []),
          subheadlines: get(task, 'data.builder.subHeadlines', []),
          descriptions: get(task, 'data.builder.descriptions', []),
          display_name: get(task, 'data.campaigns[0].ads[0].display_name', ''),
          cta: get(task, 'data.campaigns[0].ads[0].call_to_action', ''),
          campaignsByAdGroup: get(task, 'data.campaignsByAdGroup', {}),
          canCreateAdsByAdGroup: get(task, 'data.canCreateAdsByAdGroup', false),
          adsFilteredByAdGroup: get(task, 'data.adsFilteredByAdGroup', {}),
        }}
        status={get(task, 'status')}
        refetch={refetch}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Campaign Wizard Wrapper (Card + step tracking + taskId badge)
// ═══════════════════════════════════════════════════════════════

function CampaignWizardWrapper() {
  const [currentStepTitle, setCurrentStepTitle] = useState('Campaign Wizard');
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('id') ?? '';
  const { setShowSidebar } = useSidebar();

  useEffect(() => {
    setShowSidebar(true);
  }, [setShowSidebar]);

  return (
    <div className="w-full py-6">
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Campaign Wizard - {currentStepTitle}</h1>
          {taskId && (
            <CampaignWizardTaskBadge taskId={taskId} />
          )}
        </div>
        <Suspense fallback={<TabLoader />}>
          <CampaignWizard
            onStepChange={(_step: any, stepData: any) => {
              setCurrentStepTitle(stepData?.title || 'Campaign Wizard');
            }}
            isAdmin={true}
          />
        </Suspense>
      </Card>
    </div>
  );
}

function CampaignWizardTaskBadge({ taskId }: { taskId: string }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex">
            <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 flex items-center gap-2 cursor-help">
              <Info size={14} className="text-yellow-600" />
              Auto Saved Draft: {taskId}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs p-3 z-50">
          <p className="text-sm">This campaign is automatically saved as a draft as you work.</p>
          <p className="text-sm mt-1">You can access all your drafts by visiting the campaigns creation tasks page.</p>
          <Link
            to="/dashboard/drafts"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-2"
          >
            View all drafts <ExternalLink size={12} />
          </Link>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ═══════════════════════════════════════════════════════════════
// Knowledge Editor Wrapper (with takeover)
// ═══════════════════════════════════════════════════════════════

function KnowledgeEditorWrapper() {
  const { id } = useParams();

  if (!id) {
    return <Navigate to="/knowledge" replace />;
  }

  return (
    <div className="w-full h-full">
      <Suspense fallback={<PageLoader />}>
        <KnowledgeEditorWithTakeover
          knowledgeId={id}
          takeoverConfig={{
            id,
            channelName: `knowledge-${id}`,
          }}
        />
      </Suspense>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Redirect Helper Components
// ═══════════════════════════════════════════════════════════════

function RedirectWithParams({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function InteractionLookupRedirect() {
  const { id } = useParams();
  return <Navigate to={`/dashboard/interaction-lookup?_id=${id}`} replace />;
}

function CampaignEditRedirect({ platform }: { platform: string }) {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/dashboard/campaigns-edit/${platform}/${id}${search}`} replace />;
}

// ═══════════════════════════════════════════════════════════════
// Reports Menu Page
// ═══════════════════════════════════════════════════════════════

const reportsItems = [
  {
    name: 'Drilldown',
    path: '/reports/drilldown',
    description: 'Unlock insights with Drilldown Reports. Analyze data by Campaign, Ad Source, Browser, Geolocation, and more. AI enhances analysis.',
    icon: ChartColumn,
  },
  {
    name: 'Interactions Report',
    path: '/reports/interactions',
    description: 'Dive deep with Interactions Report. Explore Campaigns, Ads, Sources, Browsers, Geolocation, and conversions. Filter for insights.',
    icon: Table2,
  },
  {
    name: 'Interaction Lookup',
    path: '/dashboard/interaction-lookup',
    description: 'Unlock insights with Interaction Lookup. Retrieve detailed data on Campaigns, Ads, Sources, Browsers, Geolocation, and conversions. Optimize strategies.',
    icon: Search,
  },
];

function ReportsMenuPage() {
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <ChartColumn className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Reports</h1>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportsItems.map((item) => (
          <Link key={item.path} to={item.path}>
            <Card className="p-4 hover:bg-accent/5 transition-colors cursor-pointer">
              <div className="flex gap-3">
                <div className="mt-0.5 text-primary">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium">{item.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// More Options Page
// ═══════════════════════════════════════════════════════════════

function MorePage() {
  const [isCollapsedOpen, setIsCollapsedOpen] = useState(false);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">More Options</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {moreItems.map((item) => (
          <Link key={item.path} to={item.path}>
            <Card className="p-4 hover:bg-accent/5 transition-colors cursor-pointer">
              <div className="flex gap-3">
                <div className="mt-0.5 text-primary">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-medium">{item.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {item.description}
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-8">
        <Button
          variant="ghost"
          className="w-full flex items-center justify-between p-4 mb-4"
          onClick={() => setIsCollapsedOpen(!isCollapsedOpen)}
        >
          <span className="font-medium">Additional Options</span>
          {isCollapsedOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>

        {isCollapsedOpen && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collapsedMoreItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Card className="p-4 hover:bg-accent/5 transition-colors cursor-pointer">
                  <div className="flex gap-3">
                    <div className="mt-0.5 text-primary">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
